import React from 'react';
import { ConditionType, Filter, Select, TextInput } from '@fabric-ui/components';
import { Transition } from '@fabric-ui/icons';
import { Box } from '@mui/material';
import DateTypeBox from '../DateTypeBox';
import { getDefaultFromToFilterOperator, getFromToFilterOperatorOptions, isDateFieldType } from '../fromToFilterUtils';

interface FromToFilterCondition {
  changes?: {
    from?: string;
    to?: string;
    fromOperator?: string;
    toOperator?: string;
  };
}

interface FromToFilterModeValueInputProps {
  actualFilterValue: Filter;
  enableFilter: boolean;
  disabled: boolean;
  readOnly: boolean;
  fieldName?: string | null;
  fieldMap: Record<string, any>;
  typeCondition: ConditionType;
  typeName: string;
  operatorOptions: Record<string, any>;
  size: 'small' | 'xSmall' | 'medium';
  setEffectiveFilter: (filter: Filter) => void;
}

export const FromToFilterModeValueInput: React.FC<FromToFilterModeValueInputProps> = React.memo(
  ({
    actualFilterValue,
    enableFilter,
    disabled,
    readOnly,
    fieldName,
    fieldMap,
    typeCondition,
    typeName,
    operatorOptions,
    size,
    setEffectiveFilter,
  }) => {
    const condition = actualFilterValue.expression?.[typeCondition] as FromToFilterCondition | undefined;

    const fromValue = condition?.changes?.from ?? '';
    const toValue = condition?.changes?.to ?? '';

    // Check if this is a date field - handle with date-specific inputs in fromToFilter mode
    const isDateFilter = isDateFieldType(fieldName, fieldMap);

    const defaultOperator = getDefaultFromToFilterOperator(isDateFilter);
    const fromOperator = condition?.changes?.fromOperator ?? defaultOperator;
    const toOperator = condition?.changes?.toOperator ?? defaultOperator;

    const operatorOptionsToUse = getFromToFilterOperatorOptions(typeCondition, typeName, operatorOptions);

    const setFromOperator = (value: string) => {
      setEffectiveFilter({
        ...actualFilterValue,
        expression: {
          ...actualFilterValue.expression,
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          [typeCondition]: {
            ...(condition || {}),
            changes: {
              ...(condition?.changes || {}),
              fromOperator: value,
            },
          },
        },
      });
    };

    const setToOperator = (value: string) => {
      setEffectiveFilter({
        ...actualFilterValue,
        expression: {
          ...actualFilterValue.expression,
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          [typeCondition]: {
            ...(condition || {}),
            changes: {
              ...(condition?.changes || {}),
              toOperator: value,
            },
          },
        },
      });
    };

    // For date fields, use DateTypeBox with fromToFilter mode adapters
    if (isDateFilter) {
      // Create adapter functions to translate between fromToFilter structure and DateTypeBox structure
      const createFromDateFilter = (): Filter => ({
        expression: {
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          stringCondition: {
            equals: fromValue as string,
          },
        },
      });

      const createToDateFilter = (): Filter => ({
        expression: {
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          stringCondition: {
            equals: toValue as string,
          },
        },
      });

      const setFromDateFilter = (newFilter: Filter) => {
        const newDateValue = newFilter.expression?.stringCondition?.equals || '';
        setEffectiveFilter({
          ...actualFilterValue,
          expression: {
            ...actualFilterValue.expression,
            fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
            [typeCondition]: {
              ...(condition || {}),
              changes: {
                ...(condition?.changes || {}),
                from: newDateValue,
              },
            },
          },
        });
      };

      const setToDateFilter = (newFilter: Filter) => {
        const newDateValue = newFilter.expression?.stringCondition?.equals || '';
        setEffectiveFilter({
          ...actualFilterValue,
          expression: {
            ...actualFilterValue.expression,
            fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
            [typeCondition]: {
              ...(condition || {}),
              changes: {
                ...(condition?.changes || {}),
                to: newDateValue,
              },
            },
          },
        });
      };

      return (
        <>
          {/* From section: operator + date */}
          <Select
            size={size}
            style={{ width: 150, input: { fontWeight: 600 } }}
            isRequired
            muiProps={{ disablePortal: true }}
            placeholder="Select Operator"
            value={fromOperator}
            onChange={setFromOperator}
            disabled={!enableFilter || disabled || readOnly}
            options={operatorOptionsToUse}
          />
          <DateTypeBox filter={createFromDateFilter()} setFilter={setFromDateFilter} />
          {/* Separator icon */}
          <Box className="flex h-full items-center justify-center border-gray-200">
            <Transition fontSize="inherit" />
          </Box>
          {/* To section: operator + date */}
          <Select
            size={size}
            style={{ width: 150, input: { fontWeight: 600 } }}
            isRequired
            muiProps={{ disablePortal: true }}
            placeholder="Select Operator"
            value={toOperator}
            onChange={setToOperator}
            disabled={!enableFilter || disabled || readOnly}
            options={operatorOptionsToUse}
          />
          <DateTypeBox filter={createToDateFilter()} setFilter={setToDateFilter} />
        </>
      );
    }

    const setFromValue = (value: string) => {
      setEffectiveFilter({
        ...actualFilterValue,
        expression: {
          ...actualFilterValue.expression,
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          [typeCondition]: {
            ...(condition || {}),
            changes: {
              ...(condition?.changes || {}),
              from: value,
            },
          },
        },
      });
    };

    const setToValue = (value: string) => {
      setEffectiveFilter({
        ...actualFilterValue,
        expression: {
          ...actualFilterValue.expression,
          fieldName: fieldName || actualFilterValue.expression?.fieldName || '',
          [typeCondition]: {
            ...(condition || {}),
            changes: {
              ...(condition?.changes || {}),
              to: value,
            },
          },
        },
      });
    };

    return (
      <>
        {/* From section: operator + value */}
        <Select
          size={size}
          style={{ width: 150, input: { fontWeight: 600 } }}
          isRequired
          muiProps={{ disablePortal: true }}
          placeholder="Select Operator"
          value={fromOperator}
          onChange={setFromOperator}
          disabled={!enableFilter || disabled || readOnly}
          options={operatorOptionsToUse}
        />
        <TextInput
          size={size}
          value={fromValue}
          onChange={setFromValue}
          disabled={!enableFilter || disabled || readOnly}
          readOnly={readOnly}
          placeholder="From"
          sx={{ width: 143 }}
        />
        {/* Separator icon */}
        <Box className="flex h-full items-center justify-center border-gray-200">
          <Transition fontSize="inherit" />
        </Box>
        {/* To section: operator + value */}
        <Select
          size={size}
          style={{ width: 150, input: { fontWeight: 600 } }}
          isRequired
          muiProps={{ disablePortal: true }}
          placeholder="Select Operator"
          value={toOperator}
          onChange={setToOperator}
          disabled={!enableFilter || disabled || readOnly}
          options={operatorOptionsToUse}
        />
        <TextInput
          size={size}
          value={toValue}
          onChange={setToValue}
          disabled={!enableFilter || disabled || readOnly}
          readOnly={readOnly}
          placeholder="To"
          sx={{ width: 143 }}
        />
      </>
    );
  }
);
